/* ============================================================
   SINGSPACE ADMIN - CONFIGURATION BOT IA (Sobre)
   ============================================================ */

import { FileText, Save, Trash2, Upload } from 'lucide-react';
import { ContextDoc } from '../../types/whatsapp.types';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

interface BotConfigSectionProps {
  botPrompt: string;
  setBotPrompt: (val: string) => void;
  isPromptSaved: boolean;
  setIsPromptSaved: (val: boolean) => void;
  handleSavePrompt: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleUploadContext: (e: React.ChangeEvent<HTMLInputElement>) => void;
  contextDocs: ContextDoc[];
  handleRemoveDoc: (id: string) => void;
}

export function BotConfigSection({
  botPrompt,
  setBotPrompt,
  isPromptSaved,
  setIsPromptSaved,
  handleSavePrompt,
  fileInputRef,
  handleUploadContext,
  contextDocs,
  handleRemoveDoc,
}: BotConfigSectionProps) {
  return (
    <div className="space-y-6">
      {/* --- PROMPT CONFIG --- */}
      <Card className="border border-slate-200">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Instructions du Bot</h3>
            <p className="text-xs text-slate-500 mt-1">Définissez le comportement de l'assistant</p>
          </div>

          <Button
            type="button"
            onClick={handleSavePrompt}
            disabled={isPromptSaved}
            size="sm"
            className={isPromptSaved ? 'bg-slate-100 text-slate-400' : 'bg-[#00303c] hover:bg-[#00758d] text-white'}
          >
            <Save size={14} className="mr-2" />
            {isPromptSaved ? 'Enregistré' : 'Enregistrer'}
          </Button>
        </div>

        <div className="p-6">
          <textarea
            value={botPrompt}
            onChange={(e) => {
              setBotPrompt(e.target.value);
              setIsPromptSaved(false);
            }}
            className="w-full h-64 p-4 bg-slate-50 border border-slate-200 focus:border-[#00758d] focus:ring-2 focus:ring-[#00758d]/20 rounded-lg outline-none text-sm text-slate-700 resize-none"
            placeholder="Écrivez les instructions système pour l'IA..."
          />
          {!isPromptSaved && <p className="text-xs text-amber-600 mt-2">Modifications non enregistrées</p>}
        </div>
      </Card>

      {/* --- KNOWLEDGE BASE --- */}
      <Card className="border border-slate-200">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Base de Connaissances</h3>
            <p className="text-xs text-slate-500 mt-1">Documents de référence pour le bot</p>
          </div>

          <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
            <Upload size={14} className="mr-2" />
            Ajouter
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleUploadContext}
            style={{ display: 'none' }}
            accept=".pdf,.doc,.docx"
            multiple
          />
        </div>

        <div className="p-6">
          {contextDocs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-slate-400">
              <FileText size={32} className="mb-2" />
              <p className="text-xs">Aucun document chargé</p>
            </div>
          ) : (
            <div className="space-y-2">
              {contextDocs.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors"
                >
                  <div className="flex items-center gap-3 overflow-hidden">
                    <FileText size={16} className="text-slate-400 flex-shrink-0" />
                    <div className="flex flex-col overflow-hidden">
                      <span className="text-sm font-medium text-slate-700 truncate">{doc.name}</span>
                      <span className="text-xs text-slate-500">{(doc.size / 1024).toFixed(1)} KB</span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveDoc(doc.id)}
                    className="text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
